// Movement evaluators. Translate (target distance + Position Policy anchor)
// into a final desired position. Movement is positional only - it does not
// decide whether to fire skills (Tempo handles that). See docs/04 §4.10 and
// docs/05-combat-sim §5.6.0.
import { add, direction, distance, scale } from './types.js';
const ANCHOR_RADIUS = 3;
const DISTANCE_GOALS = {
    stick: 0,
    short: 5,
    mid: 15,
    long: 30,
};
const KITE_TANGENT_BIAS = 0.6;
/**
 * Compute the position the entity should head toward this tick. The anchor
 * (from Position Policy) is treated as a soft constraint: we stay within
 * ANCHOR_RADIUS of it whenever target-distance maintenance would push us out.
 */
export function computeMovement(self, state, rule, anchor, targetId) {
    const target = targetId !== null ? state.entities.find((e) => e.id === targetId) : undefined;
    switch (rule) {
        case 'move_anchor_lock':
            // Stay on the anchor regardless of target.
            return anchor;
        case 'move_melee_stick':
            return desiredAtDistance(self, target, anchor, DISTANCE_GOALS.stick);
        case 'move_distance_short':
            return desiredAtDistance(self, target, anchor, DISTANCE_GOALS.short);
        case 'move_distance_mid':
            return desiredAtDistance(self, target, anchor, DISTANCE_GOALS.mid);
        case 'move_distance_long':
            return desiredAtDistance(self, target, anchor, DISTANCE_GOALS.long);
        case 'move_kite_circle': {
            if (!target)
                return anchor;
            // Stay at mid-range plus a tangent bias to circle the target.
            const radial = direction(target.pos, self.pos);
            const tangent = { x: -radial.y, y: radial.x };
            const desired = {
                x: target.pos.x + radial.x * DISTANCE_GOALS.mid + tangent.x * KITE_TANGENT_BIAS,
                y: target.pos.y + radial.y * DISTANCE_GOALS.mid + tangent.y * KITE_TANGENT_BIAS,
            };
            return clampToAnchor(desired, anchor);
        }
    }
}
function desiredAtDistance(self, target, anchor, goal) {
    if (!target) {
        return anchor;
    }
    const offset = direction(target.pos, self.pos); // points from target → self
    const desired = add(target.pos, scale(offset, goal));
    return clampToAnchor(desired, anchor);
}
/** Pull `desired` back toward `anchor` if it would leave the anchor radius. */
function clampToAnchor(desired, anchor) {
    const d = distance(desired, anchor);
    if (d <= ANCHOR_RADIUS)
        return desired;
    const dir = direction(anchor, desired);
    return {
        x: anchor.x + dir.x * ANCHOR_RADIUS,
        y: anchor.y + dir.y * ANCHOR_RADIUS,
    };
}
//# sourceMappingURL=movement.js.map